import { CdkDrag, CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ListComponent } from '@common/components/base/list';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { AppService } from '@src/suite/app.service';
import { CastService } from '@src/suite/cast/services/cast-service';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { CastViewStateComponent } from '@src/suite/common/components/view-state/cast-view-state';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { Record } from '@src/suite/common/data-models/records/record';
import { TreeLikeFlatNode } from '@src/suite/common/data-models/search-results/search-results';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { CastActions, CastApiActions } from '@src/suite/common/store/cast/cast.actions';
import {
    selectCastLayoutOptions,
    selectCurrentCardItemHeights,
    selectCurrentCastViewState,
    selectCurrentPublicationChannel,
    selectCurrentPublicationList,
    selectCurrentPublicationListSectionKeys,
    selectHasMoreThanOneSection,
    selectIsDragStarted,
    selectListLoading,
    selectProcessing,
    selectTreeControl,
    selectTreeDataSource,
    selectViewDateRange,
} from '@src/suite/common/store/cast/cast.selectors';
import { NEW_SECTION_ID_PREFIX, SECTION_ID_PREFIX } from '@src/suite/common/store/cast/cast.utils';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import {
    selectCurrentLayout,
    selectViewStateMaxPublicationRevisionNumber,
    selectViewStatePublicationRevisionNumber,
    selectViewStatePublicationRevisionsList,
} from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { selectTopicTemplates } from '@src/suite/common/store/records/records.selectors';
import { TopicCustomFieldsComponent } from '@src/suite/playbook/components/topic-custom-fields/topic-custom-fields';
import { filter, map, take } from 'rxjs';

@Component({
    selector: 'st-cast-list',
    templateUrl: './cast-list.html',
    styleUrls: ['./cast-list.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-cast-list',
    },
})
export class CastListComponent extends ListComponent implements OnInit {
    visualStateService = inject(VisualStateService);
    castService = inject(CastService);
    modalService = inject(ModalService);
    nomWebServicesJson = inject(NomWebServicesJson);
    dialog = inject(MatDialog);
    appService = inject(AppService);

    currentPeriod = this.store.selectSignal(selectViewDateRange);

    dataSource = this.store.selectSignal(selectTreeDataSource);
    viewStateLayoutValue = this.store.selectSignal(selectCurrentLayout);
    treeControl = this.store.selectSignal(selectTreeControl);
    // normalItemHeights = this.store.selectSignal(selectCurrentNormalCardItemHeights);
    // condensedItemHeights = this.store.selectSignal(selectCurrentCondesedItemHeights);
    // customFieldsItemHeights = this.store.selectSignal(selectCurrentCardItemCustomFieldsHeights);

    cardItemHeights = this.store.selectSignal(selectCurrentCardItemHeights);
    vs = this.store.selectSignal(selectCurrentCastViewState);
    selectIsDragStarted = this.store.selectSignal(selectIsDragStarted);
    channel = this.store.selectSignal(selectCurrentPublicationChannel);
    publicationList = this.store.selectSignal(selectCurrentPublicationList);
    maxPublicationRevisionNumber = this.store.selectSignal(selectViewStateMaxPublicationRevisionNumber);
    publicationRevisionNumber = this.store.selectSignal(selectViewStatePublicationRevisionNumber);
    publicationRevisionsList = this.store.selectSignal(selectViewStatePublicationRevisionsList);
    isProcessing = this.store.selectSignal(selectProcessing);
    listLoading = this.store.selectSignal(selectListLoading);
    hasMoreThanOneSection = this.store.selectSignal(selectHasMoreThanOneSection);
    viewStateLayoutOptions = this.store.selectSignal(selectCastLayoutOptions);
    topicTemplates = this.store.selectSignal(selectTopicTemplates);
    sectionKeys = this.store.selectSignal(selectCurrentPublicationListSectionKeys);

    draggedObject = signal<StorytellingRecord>(null);
    editedSections = signal<Map<string, string>>(new Map<string, string>());

    isReadonly = computed(() => {
        return this.publicationRevisionNumber() < this.maxPublicationRevisionNumber() || !!this.publicationList()?.PSTPublicationListPublishedTime?.moment?.isValid();
    });

    // itemHeights = computed(() => {
    //     if (this.viewStateLayoutValue() === ViewStateLayouts.CONDENSE) {
    //         return this?.condensedItemHeights();
    //     }
    //     if (this.shouldShowCustomFields()) {
    //         return this.customFieldsItemHeights();
    //     }
    //     return this.normalItemHeights();
    // });

    shouldShowCustomFields = computed(() => {
        return this.viewStateCustomCondensedItems()?.length > 0 && !this.visualStateService.isLessThanMedium;
    });

    viewStateCustomCondensedItems = computed(() => {
        const vs = this.vs();
        // TODO: not sure we need this check - || _vs instanceof TopicViewStateRecord || _vs instanceof SingleFeedViewStateRecord
        if (vs instanceof PublicationViewStateRecord) {
            return vs.viewStateCustomCardItems;
        }
        return [];
    });

    publicationStatusLabel = computed(() => {
        const publicationDate = this.publicationList()?.PSTPublicationListPublishedTime;
        if (publicationDate) {
            const userId = this.publicationList()?.PSTPublicationListPublishedBy[0];
            const user = this.globalTablesService?.Staff[userId];
            const res = this.languageSettings.PSTWebPublishedAtByPSTWebPublishedAtBy.replace('{publicationDate}', publicationDate.toDateTimeString()).replace('{userName}', user?.DisplayName);
            return res;
        }
        return this.languageSettings.PSTWebPublished;
    });

    editionName = computed(() => {
        return (this.vs() as PublicationViewStateRecord)?.editionName;
    });

    inEditMode = computed(() => {
        return this.editedSections().size > 0;
    });

    disabledCreateNewRevisionButton = computed(() => {
        return this.isProcessing() || this.publicationRevisionNumber() < this.maxPublicationRevisionNumber() || !this.channel()?.PSTPublicationChannelRevisions;
    });

    disabledPublishButton = computed(() => {
        return this.isProcessing() || this.isReadonly() || !this.publicationList() || this.publicationList()?.children?.length === 0;
    });

    saveAsViewSettingsEnabled = computed(() => {
        return true;
    });

    saveViewSettingsEnabled = computed(() => {
        if (this.vs()?.path?.isTeamView && !this.nomWebServicesJson?.HelloResults?.Supervisor) return false;
        return true;
    });

    topicTemplatesDisabled = computed(() => {
        return !this.topicTemplates().length || !this.topicEditable;
    });

    isRTL = computed(() => {
        return this.globalTablesService.getRtl();
    });

    newSectionId = `${NEW_SECTION_ID_PREFIX}${this.languageSettings.PSTWebNewSectionLabel}`;
    topicEditable: boolean = true;

    public topicCardCustomFieldsDialogRef: MatDialogRef<TopicCustomFieldsComponent, any> = null;

    ngOnInit() {
        this.store.dispatch(CastApiActions.loadPublicationsChannels());
        this.store.dispatch(PlaybookApiActions.loadTopicTemplates());
        this.topicEditable = this.globalTablesService.securityPrivileges[this.nomWebServicesJson.PSTGroup].topic.write;
    }

    public trackByGuid(_, node) {
        if (node) {
            return node.id;
        }
    }

    toggleNode(node: TreeLikeFlatNode<StorytellingRecord>) {
        this.store.dispatch(ListActions.changeNodeExpandedState({ nodeUuid: node?.id, isExpanded: !this.treeControl().isExpanded(node) }));
    }

    updateRevisionNumber(revNumber: number) {
        this.store.dispatch(ViewStateActions.updateViewStateRevisionNumber({ revisionNumber: revNumber }));
    }
    updateLayout(layout: string) {
        this.store.dispatch(ViewStateActions.updateViewStateLayout({ layout }));
    }
    updateGroupBy(groupBy: string) {
        this.store.dispatch(ViewStateActions.updateViewStateGroupBy({ groupBy }));
    }

    public onDragStarted(node: TreeLikeFlatNode<Record>) {
        super.onDragStarted(node);

        const record = <StorytellingRecord>node.object;
        this.store.dispatch(CastActions.setDragRecordGuid({ guid: node.id }));
        this.draggedObject.set(record);
    }

    publish() {
        this.editedSections().clear();
        this.store.dispatch(CastActions.resetAddingNewSection());
        this.store.dispatch(CastApiActions.publishCurrentPublicationList());
    }
    newRevision() {
        this.editedSections().clear();
        this.store.dispatch(CastActions.resetAddingNewSection());
        this.store.dispatch(CastApiActions.createNewRevisionForCurrentPublicationList());
    }

    public removeAll() {
        return this.modalService
            .openModal(this.languageSettings.PSTWebConfirmClearPublicationListLabel, this.languageSettings.PSTWebConfirmClearPublicationListMessage, 'Yes', 'No')
            .result.pipe(
                take(1),
                filter(result => result === ModalResponseCodes.OK),
                map(() => {
                    this.store.dispatch(CastApiActions.clearCurrentPublicationList());
                })
            )
            .subscribe();
    }

    public onPublicationListSectionsDrop(_event?: CdkDragDrop<any[]>, _drag?: CdkDrag) {
        if (this.targetGuid() === this.newSectionId) {
            return;
        }
        this.store.dispatch(CastApiActions.addUpdatePublicationList({ droppedGuid: this.targetGuid() }));
    }
    public addSection(index: number) {
        if (this.editedSections().has(this.newSectionId)) {
            return;
        }
        this.store.dispatch(CastActions.startAddingNewSection({ position: index }));
        this.editSection(this.newSectionId);
    }

    public deleteSection(event: Event, section: string) {
        if (section) {
            this.store.dispatch(CastApiActions.deleteSection({ section: section.replace(SECTION_ID_PREFIX, '') }));
        }
    }

    public deletePublication(publication: StorytellingPublicationRecord) {
        this.store.dispatch(CastApiActions.deletePublications({ publicationGuidsToDelete: [publication.Guid] }));
    }

    public onFullscreenHandler(node: TreeLikeFlatNode<StorytellingRecord>) {
        const data = node.object.linkedRecords[0];
        switch (data.Type) {
            case ObjectTypes.TOPIC:
                this.store.dispatch(OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: data.Guid, recordType: data.Type, currentNodeUUID: node.uniqueId }));
                break;
            case ObjectTypes.ASSIGNMENT:
                this.store.dispatch(
                    OpenFullscreenModalActions.openAssignmentFullscreenDialog({
                        recordGuid: data.Guid,
                        recordType: data.Type,
                        currentNodeUUID: node.uniqueId,
                        sidecarMenu: FullscreenLayoutAreas.SIDECAR_MAIN,
                        isMetadata: true,
                    })
                );
                break;
        }
    }
    public editSection(section: string) {
        let sectionValue = section;
        if (section.startsWith(NEW_SECTION_ID_PREFIX)) {
            sectionValue = section.replace(NEW_SECTION_ID_PREFIX, '');
        } else if (section.startsWith(SECTION_ID_PREFIX)) {
            sectionValue = section.replace(SECTION_ID_PREFIX, '');
        }
        this.editedSections().set(section, sectionValue);
    }

    public cancelEditSection(section: string) {
        this.editedSections().delete(section);
        this.store.dispatch(CastActions.resetAddingNewSection());
    }
    public saveEditSection(section: string, idx: number) {
        if (section === this.newSectionId) {
            this.addNewSection(section, idx);
        } else {
            this.updateOldSection(section);
        }
    }

    public updateOldSection(section: string) {
        const sectionValue = this.editedSections().get(section);
        const oldSectionValue = section.replace(SECTION_ID_PREFIX, '');

        if (sectionValue && sectionValue === oldSectionValue) {
            this.editedSections().delete(section);
            return;
        }

        if (!sectionValue || !this.isSectionTitleValid(sectionValue, oldSectionValue)) {
            return;
        }
        this.store.dispatch(CastApiActions.renamePublicationListSection({ oldSection: oldSectionValue, newSection: sectionValue }));
        this.editedSections().delete(section);
    }

    public addNewSection(section: string, idx: number) {
        const sectionValue = this.editedSections().get(section);
        if (!sectionValue || !this.isSectionTitleValid(sectionValue, section)) {
            return;
        }
        this.store.dispatch(CastApiActions.addPublicationListSection({ section: this.editedSections().get(section), idx: idx }));
        this.editedSections().delete(section);
    }

    expandCollapseAll(node: TreeLikeFlatNode<StorytellingRecord>) {
        const expanderNodes = this.treeControl().dataNodes.filter(n => n.level === 0);
        this.store.dispatch(ListActions.changeAllNodesExpandedState({ expanderNodes: expanderNodes, isExpanded: !this.treeControl().isExpanded(node) }));
    }

    public onSectionNameChanged(value: string, section: string) {
        this.editedSections().set(section, value);
    }

    public saveViewState() {
        this.store.dispatch(NavigationApiActions.saveCurrentNavigationItem());
    }
    public async saveAsViewState() {
        this.dialog.closeAll();
        await this.dialog.afterAllClosed.pipe(take(1)).toPromise();
        const vs = this.vs();
        let group = this._group;
        let section = this._section;
        let item = this._item;
        if (!this.nomWebServicesJson.HelloResults.Supervisor) {
            // Make sure new view is created as personal view
            group = null;
            section = null;
            item = null;
        }
        this.dialog.open(CastViewStateComponent, {
            width: '40%',
            hasBackdrop: true,
            panelClass: 'st-view-state-dialog',
            data: {
                copy: true,
                edit: false,
                vs,
                group,
                section,
                item,
            },
        });
    }
    public async openTopicCardCustomFieldsDialog() {
        this.dialog.closeAll();
        await this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
            this.topicCardCustomFieldsDialogRef = this.dialog.open(TopicCustomFieldsComponent, {
                hasBackdrop: true,
                disableClose: false,
                panelClass: 'desktop',
                minWidth: '1202px',
                maxWidth: '100%',
            });
            this.topicCardCustomFieldsDialogRef.componentInstance.changeDetectorRef.detectChanges();
        });
    }

    public async selectTopicTemplate(template: StorytellingTopicRecord) {
        this.store.dispatch(
            OpenFullscreenModalActions.initCreateNewRecordFullscreen({
                recordTemplate: template,
                recordType: template.Type,
            })
        );
    }

    public deleteTemplate(data: StorytellingTopicRecord | StorytellingStoryRecord) {
        this.store.dispatch(RecordHttpActions.deleteRecordTemplate({ guid: data.Guid, recordType: data.Type }));
    }

    public async onNewTopicClicked() {
        this.store.dispatch(
            OpenFullscreenModalActions.initCreateNewRecordFullscreen({
                recordType: ObjectTypes.TOPIC,
            })
        );
    }

    public onTopicTemplateMenuOpened() {
        this.store.dispatch(PlaybookApiActions.loadTopicTemplates());
    }

    private isSectionTitleValid(newTitle: string, section: string): boolean {
        // Empty label
        if (!newTitle.trim()) {
            this.modalService.openAlert(this.languageSettings.PSTWebCastErrorEditingSectionLabel, this.languageSettings.PSTWebCastSectionEmptyText);
            return false;
        }
        const otherSections = this.sectionKeys().filter(s => s !== section);
        // Existing label
        const exists = otherSections.includes(newTitle.trim());
        if (exists) {
            this.modalService.openAlert(this.languageSettings.PSTWebCastErrorEditingSectionLabel, this.languageSettings.PSTWebCastSectionExistedText);
            return false;
        }

        return true;
    }
}

// TODO FIX THIS SHIT
// @ViewChild('btn1') public button;
// @ViewChild('mobileTopBar', { read: TemplateRef, static: true }) mobileTopBar;
// @ViewChild('dateRangePager') public dateRangePager: DateRangePagerComponent;
// @ViewChildren(CastItemComponent) castItemsRef: QueryList<CastItemComponent>;
// public dialogRef: MatDialogRef<StFullScreenDialogComponent, any> = null;
// public viewStateFilterPanelStates = ViewStateFilterStates;
// public viewStateFilterVisibility = ViewStateFilterVisibility;
// public viewStateLayouts = ViewStateLayouts;
// public viewStateTypes = ViewStateTypes;
// public pstFieldNames = PSTFieldNames;
// public isLastEditedSectionTitleValid: boolean = true;
// public requestInProgress: boolean = false;
// public savedViewDropdownVisible: boolean = true;
// get totalGroupCount$(): Observable<number> {
//     return this.viewStateGroupByValue$.pipe(
//         map(groupBy => {
//             if (this.castService.castSearchResults.publicationGroups2[groupBy]) {
//                 return this.castService.castSearchResults.publicationGroups2[groupBy].keys.length;
//             }
//             return 0;
//         })
//     );
// }
// public inSearchMode: boolean = false;
// public inTagsSearchMode: boolean = false;
// public inUsersSearchMode: boolean = false;
// public searchTerm: string = '';
// public searchTermUser: string = '';
// public inSlideshowMode: boolean = false;
// public isReadonly: boolean = false;
// public clearAllBtnDisabled: boolean = true;
// public topicEditable: boolean = true;
// public editedSections: Array<string> = [];
// public editedSectionsLabels: { [name: string]: string } = {};
// public isSectionInEditMode(guid: string): boolean {
//     return this.editedSections.indexOf(guid) > -1;
// }
// public get inEditMode(): boolean {
//     return this.editedSections.length > 0;
// }
// public editSection(event: Event, title: string) {
//     event.stopPropagation();
//     this.editedSections.push(title);
//     this.editedSectionsLabels[title] = title;
// }
// public cancelEditSection(event: Event, label: string) {
//     if (label === this.languageSettings.PSTWebNewSectionLabel) {
//         delete this.deletedChannelSections[label];
//         this.castService.castSearchResults.publicationList.removeSection(label);
//         this.rebuildChannelSections();
//     }
//     event.stopPropagation();
//     this.exitEditSectionMode(label);
// }
// public async saveEditSection(event: Event, label: string) {
//     event.stopPropagation();
//     if (!this.isSectionTitleValid(label, this.editedSectionsLabels[label])) {
//         return;
//     }
//     await this.onSectionEdited(label, this.editedSectionsLabels[label]);
//     await this.updateSequence(this.castService.channelSections);
//     this.exitEditSectionMode(label);
// }
// public exitEditSectionMode(label: string) {
//     const idx: number = this.editedSections.indexOf(label);
//     if (idx > -1) {
//         this.editedSections.splice(idx, 1);
//     }
// }
// public onSectionNameChanged(value: any, label: string) {
//     this.editedSectionsLabels[label] = value;
// }
// public async addSection(event: Event, index: number) {
//     event.stopPropagation();
//     const title = this.languageSettings.PSTWebNewSectionLabel;
//     await this.onSectionAdded(title, index);
//     this.rebuildChannelSections();
//     this.editSection(event, title);
// }
// public async deleteSection(event: Event, section: string) {
//     if (this.requestInProgress) {
//         return;
//     }
//     const modalRef = this.modalService.openModal(this.languageSettings.PSTWebConfirmDeleteSectionLabel, this.languageSettings.PSTWebConfirmDeleteSectionMessage, 'Yes', 'No');
//     let modalRes;
//     try {
//         modalRes = await firstValueFrom(modalRef.result);
//     } catch (er) {
//         modalRes = ModalResponseCodes.CANCEL;
//     }
//     if (modalRes === ModalResponseCodes.OK) {
//         this.showLoader();
//         await this.createPublicationList();
//         this.deletedChannelSections[section] = this.castService.channelSections[section];
//         this.castService.castSearchResults.publicationList.removeSection(section);
//         await this.deleteSections();
//         await this.updateSequence(this.castService.channelSections);
//         this.rebuildChannelSections();
//         this.hideLoader();
//     }
// }
// get ObjectTypesEnum(): any {
//     return ObjectTypesEnum;
// }
// get viewStateItems(): string {
//     return this.ObjectTypesEnum === 137 ? this.languageSettings.PSTWebTopicsLabel.toLowerCase() : this.languageSettings.PSTWebAssignmentsLabel.toLowerCase();
// }
// get saveAsViewSettingsEnabled(): boolean {
//     return true;
// }
// get saveViewSettingsEnabled(): boolean {
//     if (this._vs?.path?.isTeamView && !this.nomWebServicesJson?.HelloResults?.Supervisor) return false;
//     return true;
// }
// get topicTemplatesDisabled(): boolean {
//     return !(this.playbookService?.groupTopicTemplates?.length || this.playbookService.personalTopicTemplates?.length) || !this.topicEditable;
// }
// get topicTemplates(): StorytellingTopicRecord[] {
//     return this.playbookSearchService.topicTemplates;
// }
// public ObjectTypesEnum = ObjectTypesEnum;
// public selectedSavedView: ViewStateRecord = null;
// public deletedChannelSections: { [id: string]: Array<StorytellingPublicationRecord> } = {};
// public getPlaybookViewStateRecords(): Array<ViewStateRecord> {
//     return DataStore.getSingleViewStates('playbook');
// }
// public getPublicationChannelRecords(): Array<StorytellingPublicationChannelRecord> {
//     return DataStore.publicationChannelsResults.channels;
// }
// public onMenuOpened() {
//     this.playbookService.loadTopicTemplates(this._vs);
//     this.playbookService.loadTopicTemplates(this._vs);
// }
// async onNewTopicOpened() {
//     this.store.dispatch(OpenFullscreenModalActions.castListCreateNewTopicFullscreen());
// }
// public topicCardCustomFieldsDialogRef: MatDialogRef<TopicCustomFieldsComponent, any> = null;
// viewStateLayoutValue$ = this.store.select(selectCurrentLayout);
// viewStateLayoutOptions$ = this.store.select(selectCastLayoutOptions);
// viewStateGroupingOptions$ = this.store.select(selectCastGroupingOptions);
// maxPublicationRevisionNumber$ = this.store.select(selectViewStateMaxPublicationRevisionNumber);
// publicationRevisionNumber$ = this.store.select(selectViewStatePublicationRevisionNumber);
// publicationChannel$ = this.store.select(selectViewStatePublicationChannel);
// publicationRevisionsList$ = this.store.select(selectViewStatePublicationRevisionsList);
// viewStateGroupByValue$ = this.store.select(selectCurrentGroupBy);
// protected _vs: ViewStateRecord;
// vs$ = this.store
//     .select(selectCurrentViewState)
//     .pipe(
//         tap(vs => {
//             this._vs = vs ? vs : null;
//         })
//     )
//     .subscribe();
// constructor(
//     public appService: AppService,
//     public navigationService: NavigationService,
//     public layoutState: LayoutState,
//     public castService: CastService,
//     public ngZone: NgZone,
//     public changeDetectorRef: ChangeDetectorRef,
//     authService: AuthService,
//     activatedRoute: ActivatedRoute,
//     public nomWebServicesJson: NomWebServicesJson,
//     public playbookSearchService: PlaybookSearchService,
//     public visualStateService: VisualStateService,
//     public languageSettings: LanguageSettings,
//     public globalTablesService: GlobalTablesService,
//     private modalService: ModalService,
//     protected dialog: MatDialog,
//     public publicationService: PublicationService,
//     public publicationChannelsService: PublicationChannelsService,
//     public playbookService: PlaybookService,
//     protected store: Store
// ) {
//     super(authService, activatedRoute, globalTablesService, ngZone, changeDetectorRef, languageSettings, layoutState, store);
//     this.init();
//     this.navigationService.currentListView = this;
// }
// ngOnInit() {
//     DataStore.registerRTUListener('cast-list-rtu-listener', this.rtuListener.bind(this));
//     this.topicEditable = this.globalTablesService.securityPrivileges[this.nomWebServicesJson.PSTGroup].topic.write;
// }
// ngOnDestroy() {
//     super.ngOnDestroy();
//     DataStore.unregisterRTUListener('cast-list-rtu-listener');
// }
// areDatesTheSame(d1: Date, d2: Date) {
//     return d1.getUTCFullYear() === d2.getUTCFullYear() && d1.getUTCMonth() === d2.getUTCMonth() && d1.getUTCDate() === d2.getUTCDate();
// }
// private rtuListener(rec: StorytellingRecord) {
//     const viewState = <PublicationViewStateRecord>this._vs;
//     if (rec instanceof StorytellingPublicationListRecord) {
//         if (!this.castService.castSearchResults.publicationList) {
//             // first revision created
//             if (viewState.publicationChannelGUID === rec.publicationChannelGUID && this.areDatesTheSame(viewState.publicationTime.get(), rec.publicationTime.get())) {
//                 this.load(rec.publicationTime.get());
//             }
//         } else {
//             // check for newer revision
//             const currentList = this.castService.castSearchResults.publicationList;
//             if (this.areDatesTheSame(currentList.publicationTime.get(), rec.publicationTime.get())) {
//                 if (currentList.publicationChannelGUID === rec.publicationChannelGUID && (parseInt(currentList.revisionNumber) < parseInt(rec.revisionNumber) || rec.publishedTime)) {
//                     viewState.maxPublicationRevisionNumber = parseInt(rec.revisionNumber);
//                     this.isReadonly = true;
//                 } else {
//                     this.isReadonly = false;
//                 }
//             }
//         }
//     }
// }
// public get searchResults(): CastSearchResults {
//     if (this.castService.castSearchResults && !this.castService.castSearchResults.publicationListChanged) {
//         this.castService.castSearchResults.publicationListChanged = this.rebuildChannelSections.bind(this);
//     }
//     return this.castService.castSearchResults;
// }
// public get resultGroupKeys(): Array<string> {
//     return this.castService.castSearchResults.getGroups();
// }
// public get groups(): { [id: string]: ResultGroup2<StorytellingRecord> } {
//     return this.castService.castSearchResults.publicationGroups2;
// }
// public get groupDefs(): ResultGroupingDefinition {
//     return CastSearchResults.publicationGroup2Defs;
// }
// public get items(): Array<StorytellingRecord> {
//     return this.castService.castSearchResults.publicationList.children;
// }
// public get channel(): StorytellingPublicationChannelRecord {
//     return (<PublicationViewStateRecord>this._vs).publicationChannel;
// }
// public get editionName(): string {
//     return (<PublicationViewStateRecord>this._vs).editionName;
// }
// public get publicationStatusLabel(): string {
//     const publicationDate = this.searchResults?.publicationList?.publishedTime;
//     if (publicationDate) {
//         const userId = this.searchResults.publicationList.publishedBy[0];
//         const user = this.globalTablesService?.Staff[userId];
//         const res = this.languageSettings.PSTWebPublishedAtByPSTWebPublishedAtBy.replace('{publicationDate}', publicationDate.toDateTimeString()).replace('{userName}', user?.DisplayName);
//         return res;
//     }
//     return this.languageSettings.PSTWebNotPublished;
// }
// public get sections(): Array<string> {
//     if (this.castService.castSearchResults.publicationList && this.castService.castSearchResults.publicationList.publicationSequenceCollection !== undefined) {
//         return this.castService.castSearchResults.publicationList.publicationListSections;
//     }
//     return (<PublicationViewStateRecord>this._vs).publicationChannel.sectionsList;
// }
// get dataSource(): MatTreeFlatDataSource<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>> {
//     return this.castService.castSearchResults.treeDataSource;
// }
// get shouldShowCustomFields(): boolean {
//     return this.viewStateCustomCondensedItems?.length > 0 && !this.visualStateService.isLessThanMedium;
// }
// public get viewStateCustomCondensedItems(): Array<FieldDef> {
//     if (this._vs instanceof PublicationViewStateRecord || this._vs instanceof TopicViewStateRecord || this._vs instanceof SingleFeedViewStateRecord) {
//         return this._vs.viewStateCustomCardItems;
//     }
//     return [];
// }
// get itemHeights$(): Observable<number[]> {
//     return this.viewStateLayoutValue$.pipe(
//         map(value => {
//             if (value === this.viewStateLayouts.CONDENSE) {
//                 return this.castService?.castSearchResults?.condensedItemHeights;
//             }
//             if (this.shouldShowCustomFields) {
//                 return this.castService?.castSearchResults?.cardItemHeightsCustomFields;
//             }
//             return this.castService?.castSearchResults?.cardItemHeights;
//         })
//     );
// }
// get treeControl() {
//     return this.castService.castSearchResults.treeControl;
// }
// get collapseAllLabel(): string {
//     return this.languageSettings?.PSTWebCollapseAllLabel;
// }
// get expandAllLabel(): string {
//     return this.languageSettings?.PSTWebExpandAllLabel;
// }
// get disabledCreateNewRevisionButton$(): Observable<boolean> {
//     return combineLatest([this.publicationRevisionNumber$, this.maxPublicationRevisionNumber$, this.publicationChannel$]).pipe(
//         map(([revNumber, maxRevNumber, channel]) => {
//             return this.loading() || revNumber < maxRevNumber || !channel?.channelRevisions;
//         })
//     );
// }
// get disabledPublishButton(): boolean {
//     return this.loading() || this.isReadonly || this.castService?.castSearchResults?.publicationList?.getChildren()?.length === 0 || !this.castService?.castSearchResults?.publicationList;
// }
// get publicationItemsLabel(): string {
//     return this.languageSettings?.PSTWebPublicationItemsLabel;
// }
// get isRTL() {
//     return this.globalTablesService.getRtl();
// }
// public rebuildChannelSections(): void {
//     this.clearAllBtnDisabled = true;
//     this.castService.addedRecords.clear();
//     this.castService.channelSections = {};
//     if (!this.channel || !this.channel.sectionsList) {
//         return;
//     }
//     let publicationSequenceCollection: any = {};
//     if (this.searchResults.publicationList && this.searchResults.publicationList.publicationSequenceCollection) {
//         publicationSequenceCollection = JSON.parse(this.searchResults.publicationList.publicationSequenceCollection);
//     }
//     for (const section of this.sections) {
//         this.castService.channelSections[section] = new Array<StorytellingPublicationRecord>();
//         if (publicationSequenceCollection[section]) {
//             if (!Array.isArray(publicationSequenceCollection[section])) {
//                 publicationSequenceCollection[section] = publicationSequenceCollection[section].split(';');
//             }
//             for (const guid of publicationSequenceCollection[section]) {
//                 if (guid) {
//                     const publicationRecord = DataStore.byGuid<StorytellingPublicationRecord>(guid, null);
//                     if (publicationRecord) {
//                         this.castService.channelSections[section].push(publicationRecord);
//                         this.clearAllBtnDisabled = false;
//                         this.castService.addedRecords.add(publicationRecord.linkedRecord.guid);
//                     }
//                 }
//             }
//         }
//     }
//     this.castService.castSearchResults.buildTree(this.sections);
//     this.castService.rebuildChannelSections();
// }
// async publish() {
//     if (!this.isReadonly) {
//         this.showLoader();
//         const res = await this.castService.publishPublicationList(this.castService.castSearchResults.publicationList, this.channel.channelActionID, this.castService.channelSections);
//         if (res) {
//             this.isReadonly = true;
//         }
//         this.hideLoader();
//     }
// }
// async newRevision() {
//     if (this.isReadonly) {
//         this.showLoader();
//         const res = await this.castService.newRevision(this.castService.castSearchResults.publicationList);
//         if (res) {
//             if (this._vs instanceof PublicationViewStateRecord) {
//                 this._vs.publicationRevisionNumber = parseInt(res.data.revisionNumber);
//                 this._vs.maxPublicationRevisionNumber = parseInt(res.data.revisionNumber);
//             }
//         }
//         this.hideLoader();
//     }
// }
// async ngAfterViewInit() {
//     super.ngAfterViewInit();
//     // @TODO KAREV
//     //super.createSubscription(this._vsObservable, this.onViewStateRecordChange.bind(this));
//     //super.createSubscription(this._vsPropertyObservable, this.onViewStateRecordPropertyChange.bind(this));
// }
// private async init() {
//     await this.publicationChannelsService.getPublicationChannelRecords();
// }
// public async onViewStateRecordChange(vs: ViewStateRecordsAlias) {
//     try {
//         if (!vs) return;
//         await this.reInitializeView(vs);
//     } catch (e) {
//         console.log(e);
//     }
// }
// public async reInitializeView(vs?: ViewStateRecordsAlias) {
//     if (!vs) {
//         vs = this._vs;
//     }
//     if (vs && vs instanceof PublicationViewStateRecord) {
//         vs.maxPublicationRevisionNumber = -1;
//         if (this.dateRangePager) {
//             const publicationListTimeFilter = vs.viewStateFilter.filters.find(e => e.IsExactDate).get();
//             if (publicationListTimeFilter.FieldValue === PUBLICATION_LIST_TIME_TODAY) {
//                 this.dateRangePager.init();
//             } else {
//                 const offset = (<string>publicationListTimeFilter.FieldValue)[(<string>publicationListTimeFilter.FieldValue).length - 1];
//                 this.dateRangePager.firstDate = new DateTime();
//                 this.dateRangePager.firstDate.set(moment().startOf('day').add(offset, 'days').toDate());
//                 this.dateRangePager.lastDate = new DateTime();
//                 this.dateRangePager.lastDate.set(moment().startOf('day').add(offset, 'days').toDate());
//                 this.dateRangePager.updateLabel();
//                 const dt: DateTime = new DateTime();
//                 dt.set(this.dateRangePager.firstDate.get());
//                 vs.publicationTime = dt;
//             }
//         }
//         await this.loadPublicationList(true);
//         await this.layoutState.getViewStateRecords('playbook', undefined, this.globalTablesService.PlaybookTeamsArray.find(x => x.ID === this.appService.nomWebServicesJson.PSTGroup).ID);
//         await this.castService.getSavedViewItems();
//         await this.playbookService.loadTopicTemplates(this._vs);
//     }
// }
// public async onViewStateRecordPropertyChange(prop: [string, any, any]) {
//     if (!this._vs || !(this._vs instanceof PublicationViewStateRecord)) {
//         return;
//     }
//     if (this.navigationService.isNewView(this._item)) {
//         return;
//     }
//     const field = prop[0];
//     const value = prop[1];
//     const prev = prop[2];
//     if (field === PSTFieldNames.PSTPublicationChannelGUID && prev !== value) {
//         await this.reInitializeView();
//     }
//     if (field === PSTFieldNames.PSTPublicationListEditionName && prev !== value) {
//         await this.reInitializeView();
//     }
//     if (field === PSTFieldNames.PSTViewStateFilter) {
//         await this.reInitializeView();
//     }
//     if (field === PSTFieldNames.PSTPublicationTime) {
//         await this.loadPublicationList(true);
//     }
//     if (field === PSTFieldNames.PSTPublicationListRevisionNumber) {
//         await this.loadPublicationList(false);
//     }
// }
// private _editionNameTimeout: any;
// public updateEditionName(event: any) {
//     clearTimeout(this._editionNameTimeout);
//     this._editionNameTimeout = setTimeout(() => {
//         (<PublicationViewStateRecord>this._vs).editionName = event.target.value;
//     }, 500);
// }
// private _publicationListTitleTimeout: any;
// public updatePublicationListTitle(event: any) {
//     clearTimeout(this._publicationListTitleTimeout);
//     this._publicationListTitleTimeout = setTimeout(() => {
//         (<PublicationViewStateRecord>this._vs).publicationListTitle = event.target.value;
//     }, 500);
// }
// public async removeAll() {
//     if (this.requestInProgress) {
//         return;
//     }
//     const modalRef = this.castService.modalService.openModal(
//         this.languageSettings.PSTWebConfirmClearPublicationListLabel,
//         this.languageSettings.PSTWebConfirmClearPublicationListMessage,
//         'Yes',
//         'No'
//     );
//     let modalRes;
//     try {
//         modalRes = await firstValueFrom(modalRef.result);
//     } catch (er) {
//         modalRes = ModalResponseCodes.CANCEL;
//     }
//     if (modalRes === ModalResponseCodes.OK) {
//         try {
//             this.requestInProgress = true;
//             this.showLoader();
//             let guids = [];
//             for (const section of this.sections) {
//                 const channelGuids = this.castService.channelSections[section].map(rec => rec.guid);
//                 guids = [...guids, ...channelGuids];
//             }
//             const deletePublicationsArgsList = [];
//             const publicationArgs = {
//                 publicationList: this.castService.castSearchResults.publicationList,
//                 section: '',
//                 index: 0,
//                 channelSections: null,
//             };
//             deletePublicationsArgsList.push(publicationArgs);
//             const resp = await this.castService.deletePublicationRecords(deletePublicationsArgsList);
//             if (resp && resp.status) {
//                 if (this.castService.castSearchResults.publicationList) this.castService.castSearchResults.publicationList.removeChildren();
//                 const restrictedPubList = this.searchResults.publicationListsOfRestrictedChannelTypes.find(list => list.guid === this.castService.castSearchResults.publicationList.Guid);
//                 if (restrictedPubList) {
//                     restrictedPubList.removeChildren();
//                 }
//                 guids.forEach(guid => {
//                     const currentRecord: StorytellingPublicationRecord = DataStore.byGuid<StorytellingPublicationRecord>(guid, '');
//                     if (currentRecord) {
//                         DataStore.disposeRecord(currentRecord, true);
//                     }
//                 });
//             }
//             this.requestInProgress = false;
//             this.hideLoader();
//         } catch (e) {
//             this.requestInProgress = false;
//             this.hideLoader();
//         }
//     }
// }
// expandCollapseAll(node: TreeLikeFlatNode<StorytellingRecord>) {
//     if (this.treeControl.isExpanded(node)) {
//         this.treeControl.collapseAll();
//     } else {
//         this.treeControl.expandAll();
//     }
// }
// public load(date: Date) {
//     const vs: PublicationViewStateRecord = <PublicationViewStateRecord>this._vs;
//     const dt: DateTime = new DateTime();
//     dt.set(date);
//     vs.publicationTime = dt;
// }
// public async processRestrictedChannelTypes() {
//     // check if the current publication channel is restricted
//     if (this.appService.restrictedPublicationChannelTypes.indexOf(this.channel?.channelType) > -1) {
//         // get all channels of the restricted type
//         const restrictedChannelsOfTheSameType = this.getPublicationChannelRecords().filter(x => x.channelType === this.channel.channelType);
//         const channelGuids: Array<string> = restrictedChannelsOfTheSameType.map(channel => channel.Guid);
//         const vs = <PublicationViewStateRecord>this._vs;
//         // load all publication lists of the restricted channel types
//         await this.castService.loadPublicationLists(vs, channelGuids);
//     }
// }
// public async loadPublicationList(setMaxPublicationRevisionNumber: boolean) {
//     const isLoading = this.loading();
//     if (!isLoading) this.showLoader();
//     const vs = <PublicationViewStateRecord>this._vs;
//     await this.castService.loadPublicationList(vs, this.castService.availableRecords);
//     await this.processRestrictedChannelTypes();
//     if (setMaxPublicationRevisionNumber) {
//         if (this.searchResults.publicationList && this.searchResults.publicationList.revisionNumber) {
//             vs.publicationRevisionNumber = vs.maxPublicationRevisionNumber = parseInt(this.searchResults.publicationList.revisionNumber);
//         } else {
//             vs.publicationRevisionNumber = vs.maxPublicationRevisionNumber = 0;
//         }
//     }
//     if (vs.publicationRevisionNumber < vs.maxPublicationRevisionNumber || (this.searchResults.publicationList && this.searchResults.publicationList.publishedTime)) {
//         this.isReadonly = true;
//     } else {
//         this.isReadonly = false;
//     }
//     if (this.searchResults.publicationList) {
//         this.searchResults.publicationList.initializeSections();
//     }
//     this.rebuildChannelSections();
//     if (!isLoading) this.hideLoader();
// }
// public toggleSortOrder() {
//     this.store.dispatch(ViewStateActions.toggleSortOder());
// }
// private async handleRestrictedPublicationChannelTypes(publication): Promise<boolean> {
//     if (this.appService.restrictedPublicationChannelTypes.indexOf(this.channel.channelType) > -1) {
//         const existsInAnotherLists = this.searchResults.publicationListsOfRestrictedChannelTypes.filter(
//             list =>
//                 !list.publishedTime &&
//                 list.getChildren().some(child => ((<StorytellingPublicationRecord>child).linkedRecord ? (<StorytellingPublicationRecord>child).linkedRecord.Guid === publication.Guid : false))
//         );
//         const existsInTheCurrentList = existsInAnotherLists.some(
//             list => !!(this.castService.castSearchResults.publicationList && this.castService.castSearchResults.publicationList.Guid === list.Guid)
//         );
//         if (existsInAnotherLists.length > 0) {
//             if (existsInTheCurrentList) {
//                 return false;
//             }
//             // show modal
//             const res = await this.restrictedChannelTypesModal(existsInAnotherLists, publication);
//             return res;
//         }
//         if (existsInTheCurrentList) {
//             return false;
//         }
//     }
//     return true;
// }
// private async updateSequence(channelSections: { [id: string]: Array<StorytellingPublicationRecord> }) {
//     const res = await this.castService.updatePublicationListSequence(this.sections, channelSections);
//     if (!res.status) {
//         this.nomWebServicesJson.showErrorModal({
//             message: 'Unable to move / update publication within list',
//             detailMessage: `Unable to move publication. \n\rError: ${res.message}`,
//         });
//     }
// }
// private async updatePublicationRecords(event: { sourceSection: string; targetSection: string; sourceIndex: number; targetIndex: number }) {
//     await this.castService.updatePublicationRecords(event, this.castService.channelSections);
// }
// private isSectionTitleValid(oldTitle: string, newTitle: string): boolean {
//     // No label change
//     if (oldTitle === newTitle) return true;
//     // Empty label
//     if (!newTitle.trim()) {
//         this.modalService.openAlert(this.languageSettings.PSTWebCastErrorEditingSectionLabel, this.languageSettings.PSTWebCastSectionEmptyText);
//         return false;
//     }
//     // Existing label
//     const idx = Object.keys(this.castService.channelSections).indexOf(newTitle.trim());
//     if (idx > -1) {
//         this.modalService.openAlert(this.languageSettings.PSTWebCastErrorEditingSectionLabel, this.languageSettings.PSTWebCastSectionExistedText);
//         return false;
//     }
//     return true;
// }
// public async onSectionEdited(oldSectionName: string, newSectionName: string) {
//     await this.createPublicationList();
//     if (this.castService.channelSections[oldSectionName] && this.isLastEditedSectionTitleValid) {
//         this.castService.channelSections[newSectionName] = this.castService.channelSections[oldSectionName];
//         delete this.castService.channelSections[oldSectionName];
//     }
//     this.castService.castSearchResults.publicationList.editSection(oldSectionName, newSectionName);
// }
// public async onSectionAdded(title: string, index: number) {
//     if (title.trim().length === 0 || this.castService.channelSections[title.trim()]) return;
//     await this.createPublicationList();
//     this.castService.castSearchResults.publicationList.addSection(title, index + 1);
// }
// public async deleteSections() {
//     if (Object.keys(this.deletedChannelSections).length === 0) return;
//     this.requestInProgress = true;
//     try {
//         const publicationGuids: Array<string> = [];
//         const deletePublicationsArgsList = [];
//         for (const section in this.deletedChannelSections) {
//             const sectionPublications = this.deletedChannelSections[section];
//             for (const publication of sectionPublications) {
//                 publicationGuids.push(publication.Guid);
//                 const index = this.deletedChannelSections[section].findIndex(p => p.Guid === publication.Guid);
//                 const publicationArgs = {
//                     publicationList: this.castService.castSearchResults.publicationList,
//                     section,
//                     index,
//                     channelSections: this.castService.channelSections,
//                 };
//                 deletePublicationsArgsList.push(publicationArgs);
//             }
//         }
//         const res = await this.castService.deletePublicationRecords(deletePublicationsArgsList);
//         if (res && res.status) {
//             for (const guid in publicationGuids) {
//                 if (this.castService.castSearchResults.publicationList) this.castService.castSearchResults.publicationList.removeChildByGuid(guid);
//                 const pubList = this.searchResults.publicationListsOfRestrictedChannelTypes.find(list => list.Guid === this.castService.castSearchResults.publicationList.Guid);
//                 if (pubList) {
//                     pubList.removeChildByGuid(guid);
//                 }
//                 const currentRecord: StorytellingPublicationRecord = DataStore.byGuid<StorytellingPublicationRecord>(guid, '');
//                 if (currentRecord) {
//                     DataStore.disposeRecord(currentRecord, true);
//                 }
//             }
//         }
//         for (const section in this.deletedChannelSections) {
//             delete this.castService.channelSections[section];
//         }
//         this.requestInProgress = false;
//     } catch (e) {
//         console.log(e);
//         this.requestInProgress = false;
//     }
// }
// private async restrictedChannelTypesModal(publicationLists: Array<StorytellingPublicationListRecord>, publication): Promise<boolean> {
//     const listNames = publicationLists.map(list => list.Title).join(', ');
//     const dialogMessage = this.languageSettings.PSTWebConfirmAddPublicationToRestrictedChannelMessage.replace('{publicationLists}', listNames).replace('{publicationName}', publication.title);
//     const modalRef = this.modalService.openModal(this.languageSettings.PSTWebConfirmAddPublicationToRestrictedChannelLabel, dialogMessage, 'Yes', 'No');
//     let modalRes;
//     try {
//         modalRes = await firstValueFrom(modalRef.result);
//     } catch (er) {
//         modalRes = ModalResponseCodes.CANCEL;
//     }
//     if (modalRes === ModalResponseCodes.OK) {
//         this.isReadonly = true;
//         if (this.castService.castSearchResults.publicationList === null) {
//             await this.castService.createPublicationList(this._vs, this.sections, this.castService.channelSections);
//         }
//         // remove from the prior list(s) and return true to add it in the new list
//         const deletePublicationsArgsList = [];
//         const restrictedChannelPublicationLists = this.searchResults.publicationListsOfRestrictedChannelTypes.filter(
//             list =>
//                 !list.publishedTime &&
//                 list.children.some(child => ((<StorytellingPublicationRecord>child).linkedRecord ? (<StorytellingPublicationRecord>child).linkedRecord.Guid === publication.Guid : false))
//         );
//         for (const list of restrictedChannelPublicationLists) {
//             const channelSections: { [id: string]: Array<StorytellingPublicationRecord> } = {};
//             const publications = <Array<StorytellingPublicationRecord>>(
//                 list.children.filter(child => ((<StorytellingPublicationRecord>child).linkedRecord ? (<StorytellingPublicationRecord>child).linkedRecord.Guid === publication.Guid : false))
//             );
//             let publicationSequenceCollection: any = {};
//             if (list && list.publicationSequenceCollection) {
//                 publicationSequenceCollection = JSON.parse(list.publicationSequenceCollection);
//             }
//             const recordSectionMap: { [publicationRecordGuid: string]: string } = {};
//             for (const section of list.publicationListSections) {
//                 channelSections[section] = new Array<StorytellingPublicationRecord>();
//                 if (publicationSequenceCollection[section]) {
//                     if (!Array.isArray(publicationSequenceCollection[section])) {
//                         publicationSequenceCollection[section] = publicationSequenceCollection[section].split(';');
//                     }
//                     for (const guid of publicationSequenceCollection[section]) {
//                         if (guid) {
//                             const publicationRecord = publications.find(p => p.Guid === guid);
//                             if (publicationRecord) {
//                                 channelSections[section].push(publicationRecord);
//                                 recordSectionMap[publicationRecord.Guid] = section;
//                             }
//                         }
//                     }
//                 }
//             }
//             for (const publicationRecord of publications) {
//                 const section = recordSectionMap[publicationRecord.Guid];
//                 const index = channelSections[section].findIndex(p => p.Guid === publicationRecord.Guid);
//                 const publicationArgs = {
//                     publicationList: list,
//                     section,
//                     index,
//                     channelSections,
//                 };
//                 deletePublicationsArgsList.push(publicationArgs);
//             }
//         }
//         await this.castService.deletePublicationRecords(deletePublicationsArgsList);
//         if (this.castService.castSearchResults.publicationList) this.castService.castSearchResults.publicationList.removeChildByGuid(publication.guid);
//         restrictedChannelPublicationLists.forEach(list => {
//             list.getChildren().forEach(child => {
//                 if ((<StorytellingPublicationRecord>child).linkedRecord && (<StorytellingPublicationRecord>child).linkedRecord.guid === publication.guid) {
//                     list.removeChildByGuid(child.guid);
//                 }
//             });
//         });
//         this.isReadonly = false;
//         return true;
//     }
//     return false;
// }
// public async deletePublication(publication: StorytellingRecord) {
//     const modalContent = `${this.languageSettings.PSTWebConfirmDeletePart1} ${publication?.Title} ${this.languageSettings.PSTWebConfirmDeletePublcationItem}`;
//     const modalRef = this.modalService.openModal(this.languageSettings.PSTWebConfirmDeleteLabel, modalContent, this.languageSettings.PSTWebYesOption, this.languageSettings.PSTWebNoOption);
//     let modalRes;
//     try {
//         modalRes = await firstValueFrom(modalRef.result);
//     } catch (er) {
//         modalRes = ModalResponseCodes.CANCEL;
//     }
//     if (modalRes === ModalResponseCodes.OK) {
//         try {
//             this.showLoader();
//             let section: string = '';
//             let index: number = -1;
//             for (const sec in this.castService.channelSections) {
//                 let i: number = 0;
//                 for (const rec of this.castService.channelSections[sec]) {
//                     if (rec.Guid === publication.Guid) {
//                         section = sec;
//                         index = i;
//                         break;
//                     }
//                     i++;
//                 }
//             }
//             if (!this.isReadonly && section) {
//                 this.requestInProgress = true;
//                 const deletePublicationArgsList = [];
//                 const publicationArgs = {
//                     publicationList: this.castService.castSearchResults.publicationList,
//                     section,
//                     index,
//                     channelSections: this.castService.channelSections,
//                 };
//                 deletePublicationArgsList.push(publicationArgs);
//                 const resp = await this.castService.deletePublicationRecords(deletePublicationArgsList);
//                 if (resp && resp.status) {
//                     if (this.castService.castSearchResults.publicationList) this.castService.castSearchResults.publicationList.removeChildByGuid(publication.Guid);
//                     const rec = this.searchResults.publicationListsOfRestrictedChannelTypes.find(list => list.Guid === this.castService.castSearchResults.publicationList.Guid);
//                     if (rec) {
//                         rec.removeChildByGuid(publication.Guid);
//                     }
//                     const currentRecord: StorytellingPublicationRecord = DataStore.byGuid<StorytellingPublicationRecord>(publication.Guid, '');
//                     if (currentRecord) {
//                         DataStore.disposeRecord(currentRecord, true);
//                     }
//                 }
//                 this.requestInProgress = false;
//             }
//         } catch (e) {
//             this.hideLoader();
//         }
//         this.hideLoader();
//     }
// }
// public transformRecord(item: StorytellingRecord): StorytellingPublicationRecord {
//     // need to transform from a source record item - such as a Topic or Assignment, to the target Publication Record
//     const newPublication: StorytellingPublicationRecord = initializeRecord<StorytellingPublicationRecord>(ObjectTypesEnum.PUBLICATION);
//     newPublication.Title = item.Title;
//     // TODO
//     // newPublication.linkedRecords = item;
//     return newPublication;
// }
// public async openNewTopicFromTemplateDialog(topicTemplate: StorytellingTopicRecord) {
//     this.store.dispatch(
//         OpenFullscreenModalActions.castListCreateNewTopicFullscreenFromTemplate({
//             recordGuid: topicTemplate.Guid,
//             recordType: topicTemplate.Type,
//         })
//     );
// }
// public async deleteTemplate(data: StorytellingTopicRecord, list: Array<StorytellingTopicRecord>, idx: number) {
//     const modalContent = `Are you sure you want to delete the ${data.Title} template?`;
//     const modalRef = this.modalService.openModal('Delete item', modalContent, 'Yes', 'No');
//     let modalRes;
//     try {
//         modalRes = await firstValueFrom(modalRef.result);
//     } catch (er) {
//         modalRes = ModalResponseCodes.CANCEL;
//     }
//     if (modalRes === ModalResponseCodes.OK) {
//         try {
//             this.showLoader();
//             const res = await this.appService.basicContentService.deleteBasicContent(data.Database, data.Path.fullPath, data.Guid, data.Type);
//             if (res.status) {
//                 list.splice(idx, 1);
//                 // const currentRecord: TopicTemplateRecord = DataStore.byGuid<TopicTemplateRecord>(data.guid, '');
//                 // if (currentRecord) {
//                 //     DataStore.disposeRecord(currentRecord, true);
//                 // }
//             }
//         } catch (e) {
//             this.hideLoader();
//         }
//         this.hideLoader();
//     }
// }
// public trackByGuid(_, node) {
//     if (node) {
//         return node.id;
//     }
// }
// private async createPublicationList(): Promise<string> {
//     let publicationListGuid: string = null;
//     if (this.castService.castSearchResults.publicationList === null) {
//         const result = await this.castService.createPublicationList(this._vs, this.sections, this.castService.channelSections);
//         await this.loadPublicationList(true);
//         if (result.status) {
//             publicationListGuid = result.data.Guid;
//         }
//     } else {
//         publicationListGuid = this.castService.castSearchResults.publicationList.Guid;
//     }
//     return publicationListGuid;
// }
// public async onPublicationListSectionsDrop() {
//     this.castService.isCreatePublicationPending = true;
//     // Ensure publication list exists
//     const publicationListGuid = await this.createPublicationList();
//     if (!this.castService.draggedGuid || !this.castService.droppedGuid) {
//         this.castService.isCreatePublicationPending = false;
//         return;
//     }
//     let isCandidate: boolean = false;
//     let sourceSection: string = '';
//     let targetSection: string = '';
//     let sourceIndex: number = -1;
//     let targetIndex: number = -1;
//     let { draggedGuid } = this.castService;
//     // Check if dragging from candidates list
//     if (this.castService.draggedGuid.startsWith('candidate|')) {
//         isCandidate = true;
//         draggedGuid = this.castService.draggedGuid.replace('candidate|', '');
//     } else {
//         draggedGuid = this.castService.draggedGuid;
//     }
//     // Check if droping on section
//     if (this.castService.droppedGuid.startsWith('section|')) {
//         targetSection = this.castService.droppedGuid.replace('section|', '');
//         this.castService.droppedGuid = null;
//     }
//     for (const section in this.castService.channelSections) {
//         for (let i = 0; i < this.castService.channelSections[section].length; i++) {
//             if (this.castService.channelSections[section][i].Guid === this.castService.draggedGuid) {
//                 sourceSection = section;
//                 sourceIndex = i;
//             }
//             if (this.castService.channelSections[section][i].Guid === this.castService.droppedGuid) {
//                 targetSection = section;
//                 targetIndex = i + 1;
//             }
//         }
//     }
//     if (isCandidate) {
//         targetIndex = targetIndex + 1;
//     }
//     if (sourceIndex > targetIndex) {
//         targetIndex = targetIndex + 1;
//     }
//     if (targetIndex < 0) {
//         targetIndex = 0;
//     }
//     if (targetIndex > this.castService.channelSections[targetSection].length) {
//         targetIndex = this.castService.channelSections[targetSection].length;
//     }
//     if (isCandidate) {
//         const candidateRef = this.castService.filteredAvailableRecords.find((record: StorytellingRecord) => draggedGuid === record.Guid);
//         if (!candidateRef) {
//             this.castService.isCreatePublicationPending = false;
//             return;
//         }
//         const addPublicationToListOfRestrictedChannelType = await this.handleRestrictedPublicationChannelTypes(candidateRef);
//         if (!addPublicationToListOfRestrictedChannelType) {
//             this.castService.isCreatePublicationPending = false;
//             return;
//         }
//         const newPublication: StorytellingPublicationRecord = new StorytellingPublicationRecord();
//         newPublication.Title = candidateRef.Title;
//         newPublication.linkedRecord = candidateRef;
//         let createdPublication: StorytellingPublicationRecord = null;
//         let exists: boolean = false;
//         for (const rec of this.castService.channelSections[targetSection]) {
//             if (rec?.linkedRecord?.Guid === candidateRef.Guid) {
//                 exists = true;
//                 break;
//             }
//         }
//         if (!exists) {
//             this.castService.channelSections[targetSection].splice(targetIndex, 0, newPublication);
//             const guids: Array<string> = this.castService.channelSections[targetSection].slice(targetIndex + 1).map<string>(rec => rec.Guid);
//             const createdPublicationsResp: ServiceResponse<Array<StorytellingPublicationRecord>> = await this.castService.createPublications(
//                 [newPublication],
//                 targetSection,
//                 targetIndex,
//                 guids,
//                 publicationListGuid
//             );
//             if (createdPublicationsResp.status) {
//                 createdPublication = createdPublicationsResp.data[0];
//                 if (createdPublication) {
//                     this.castService.channelSections[targetSection][targetIndex] = createdPublication;
//                     createdPublication.linkedRecord = candidateRef;
//                 } else {
//                     this.castService.channelSections[targetSection].splice(targetIndex, 1);
//                 }
//             } else {
//                 this.castService.channelSections[targetSection].splice(targetIndex, 1);
//             }
//         }
//         if (!exists && createdPublication && createdPublication.linkedRecord) {
//             this.castService.addedRecords.add(createdPublication.linkedRecord.Guid);
//             createdPublication.linkedRecord.isAdded = true;
//         }
//     } else {
//         // We are droping on section itself
//         if (targetIndex < 0) targetIndex = this.castService.channelSections[targetSection].length;
//         if (sourceSection === targetSection && sourceIndex === targetIndex) {
//             this.castService.isCreatePublicationPending = false;
//             // Nothing's changed
//             return;
//         }
//         if (sourceSection !== targetSection) {
//             // Dragging cross-section. Need to check if we aren't trying to put something that already exists
//             const record: StorytellingPublicationRecord = this.castService.channelSections[sourceSection][sourceIndex];
//             let exists: boolean = false;
//             for (const rec of this.castService.channelSections[targetSection]) {
//                 if (rec?.linkedRecord?.Guid === record?.linkedRecord?.Guid) {
//                     exists = true;
//                     break;
//                 }
//             }
//             if (exists) {
//                 this.castService.isCreatePublicationPending = false;
//                 return;
//             }
//             this.castService.channelSections[sourceSection].splice(sourceIndex, 1);
//             this.castService.channelSections[targetSection].splice(targetIndex, 0, record);
//         } else {
//             // Dragging within the same section
//             this.castService.channelSections[sourceSection].splice(targetIndex, 0, this.castService.channelSections[sourceSection].splice(sourceIndex, 1)[0]);
//         }
//         this.updateSequence(this.castService.channelSections);
//         this.updatePublicationRecords({
//             sourceSection,
//             targetSection,
//             sourceIndex,
//             targetIndex,
//         });
//     }
//     this.castService.isCreatePublicationPending = false;
// }
// public draggedObject: StorytellingPublicationRecord;
// public onDragStarted(event: CdkDragStart, guid: string) {
//     this.castService.dragStarted = true;
//     this.castService.appendOnly = true;
//     this.castService.draggedGuid = guid;
//     const rec = DataStore.byGuid<StorytellingPublicationRecord>(guid, null);
//     if (rec) {
//         this.draggedObject = rec;
//     }
// }
// public onDragEnter(guid: string) {
//     this.castService.droppedGuid = guid;
// }
// public onDragEnded() {
//     super.onDragEnded();
//     this.castService.dragStarted = false;
//     this.castService.appendOnly = false;
// }
// public async openTopicCardCustomFieldsDialog() {
//     this.dialog.closeAll();
//     await this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
//         this.topicCardCustomFieldsDialogRef = this.dialog.open(TopicCustomFieldsComponent, {
//             hasBackdrop: true,
//             disableClose: false,
//             panelClass: 'desktop',
//             minWidth: '1135px',
//             maxWidth: '100%',
//         });
//     });
// }
// public saveViewState() {
//     this.store.dispatch(NavigationApiActions.saveCurrentNavigationItem());
// }
// public async saveAsViewState() {
//     this.dialog.closeAll();
//     await this.dialog.afterAllClosed.pipe(take(1)).toPromise();
//     const vs = this._vs;
//     let group = this._group;
//     let section = this._section;
//     let item = this._item;
//     if (!this.nomWebServicesJson.HelloResults.Supervisor) {
//         // Make sure new view is created as personal view
//         group = null;
//         section = null;
//         item = null;
//     }
//     this.dialog.open(CastViewStateComponent, {
//         width: '40%',
//         hasBackdrop: true,
//         panelClass: 'st-view-state-dialog',
//         data: {
//             copy: true,
//             edit: false,
//             vs,
//             group,
//             section,
//             item,
//         },
//     });
// }
// onPanelStateChange(event: boolean, node: TreeLikeFlatNode<StorytellingRecord>) {
//     if (event) this.treeControl.expand(node);
//     else this.treeControl.collapse(node);
// }
// updateRevisionNumber(revNumber: number) {
//     this.store.dispatch(ViewStateActions.updateViewStateRevisionNumber({ revisionNumber: revNumber }));
// }
// updateLayout(layout: string) {
//     this.store.dispatch(ViewStateActions.updateViewStateLayout({ layout }));
// }
// updateGroupBy(groupBy: string) {
//     this.store.dispatch(ViewStateActions.updateViewStateGroupBy({ groupBy }));
// }
